import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import { categories, getFeaturedArticles, getLatestArticles, aboutInfo } from '@/mocks/articles';

export default function Home() {
  const featuredArticles = getFeaturedArticles();
  const latestArticles = getLatestArticles(6);

  return (
    <>
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[580px] md:h-[680px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Elegant%20artistic%20abstract%20composition%20with%20flowing%20warm%20rose%20and%20gold%20gradients%2C%20soft%20ethereal%20light%20beams%2C%20minimalist%20aesthetic%2C%20dreamy%20atmosphere%2C%20cream%20background%20with%20subtle%20texture%2C%20fashion%20editorial%20style&width=1920&height=900&seq=hero-bg-home&orientation=landscape"
              alt="尤物视频 - 精选高颜值视频内容平台"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/30"></div>
          <div className="relative z-10 w-full max-w-[900px] mx-auto px-4 md:px-6 text-center">
            <p className="text-accent-300 text-sm md:text-base font-medium tracking-widest mb-4 animate-fade-in">
              精选 · 品质 · 美学
            </p>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 leading-tight mb-6">
              发现高颜值的
              <br />
              <span className="text-accent-300">视觉世界</span>
            </h1>
            <p className="text-base md:text-lg text-background-200 max-w-[600px] mx-auto mb-8 leading-relaxed">
              尤物视频汇聚海量精选视频内容，涵盖时尚、美妆、生活方式等多元品类，每天为你呈现最优质的视觉享受
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/shishang"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-compass-3-line text-lg"></i>
                探索内容
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-50/20 text-background-50 text-sm font-medium hover:bg-background-50/30 backdrop-blur-sm transition-colors whitespace-nowrap cursor-pointer"
              >
                了解更多
                <i className="ri-arrow-right-line text-lg"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                内容分类
              </h2>
              <p className="text-foreground-600 text-sm md:text-base">
                六大精品分类，总有一款适合你
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center gap-3 p-5 md:p-6 rounded-xl bg-background-100 hover:bg-primary-50 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                    <i className={`${cat.icon} text-xl md:text-2xl`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors whitespace-nowrap">
                    {cat.name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                  精选推荐
                </h2>
                <p className="text-foreground-600 text-sm">编辑团队精心甄选的优质内容</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {featuredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-50 rounded-xl overflow-hidden hover:scale-[1.02] transition-all duration-300 cursor-pointer"
                >
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="inline-block px-2.5 py-1 rounded-full text-xs font-medium bg-primary-500 text-background-50 whitespace-nowrap">
                        {categories.find(c => c.slug === article.category)?.name}
                      </span>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-xs md:text-sm text-foreground-600 line-clamp-2 mb-3">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-500">
                      <span>{article.publishDate}</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {article.views.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                  最新更新
                </h2>
                <p className="text-foreground-600 text-sm">每天都有新鲜内容等你发现</p>
              </div>
              <Link
                to="/category/shishang"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {latestArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group flex gap-4 bg-background-100 rounded-xl p-3 hover:bg-background-200/70 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-28 h-20 md:w-32 md:h-24 flex-shrink-0 rounded-lg overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="inline-block px-2 py-0.5 rounded text-[10px] font-medium bg-accent-100 text-accent-700 mb-1.5 whitespace-nowrap">
                      {categories.find(c => c.slug === article.category)?.name}
                    </span>
                    <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-1.5 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <div className="flex items-center gap-3 text-[11px] text-foreground-500">
                      <span>{article.publishDate}</span>
                      <span>{article.readTime} 阅读</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center mt-8 sm:hidden">
              <Link
                to="/category/shishang"
                className="inline-flex items-center gap-1 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                查看全部内容
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {aboutInfo.stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <p className="text-2xl md:text-3xl font-heading font-bold text-primary-600 mb-2">
                    {stat.value}
                  </p>
                  <p className="text-sm text-foreground-600">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-16 md:py-20 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="relative rounded-2xl bg-primary-500 overflow-hidden px-8 py-12 md:px-12 md:py-16 text-center">
              <div className="absolute inset-0 opacity-10">
                <img
                  src="https://readdy.ai/api/search-image?query=Soft%20abstract%20gradient%20with%20flowing%20lines%20in%20warm%20rose%20and%20cream%20tones%2C%20minimalist%20texture%2C%20elegant%20artistic%20background&width=1200&height=600&seq=cta-bg-home&orientation=landscape"
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="relative z-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-4">
                  每天发现更多精彩内容
                </h2>
                <p className="text-background-200 text-sm md:text-base max-w-[500px] mx-auto mb-8">
                  尤物视频持续更新优质内容，涵盖时尚、美妆、生活方式等多元品类，让每一天都充满灵感
                </p>
                <Link
                  to="/category/shishang"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-50 text-primary-600 text-sm font-medium hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  立即探索
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />

      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: '尤物视频',
            url: 'https://c778v.cn',
            description: '尤物视频汇聚海量高颜值精选视频内容，涵盖时尚、美妆、生活方式等多元视频内容。',
            potentialAction: {
              '@type': 'SearchAction',
              target: 'https://c778v.cn/search?q={search_term_string}',
              'query-input': 'required name=search_term_string',
            },
            publisher: {
              '@type': 'Organization',
              name: '尤物视频',
              url: 'https://c778v.cn',
            },
          }),
        }}
      />
    </>
  );
}